import { Theme } from '@eversports/react-components/design-tokens/theme'
import Flex from '@eversports/react-components/primitives/Flex'
import Heading from '@eversports/react-components/primitives/Heading'
import Icon, { Icons } from '@eversports/react-components/primitives/Icon'
import Text from '@eversports/react-components/primitives/Text'
import { makeStyles } from '@mui/styles'
import React from 'react'

import { motion } from '@eversports/react-components/design-tokens/motion'
import Page from '../../boilerplate/Page'
import Venue from '../../typings/Venue'
import Customer from '../../typings/Customer'
import AppPromotion from '../AppPromotion'
import { DesktopBookingDetails, MobileBookingDetails } from '../BookingDetails'
import BottomFixedCollapsibleSection from '../BottomFixedCollapsibleSection'

import ConfirmationActions from './ConfirmationActions'
import ConfirmationHeading from './ConfirmationHeading'
import ConfirmationSubHeading from './ConfirmationSubHeading'
import MobileConfirmationCollapsibleCardHeader from './MobileConfirmationCollapsibleCardHeader'
import NewsletterBanner from './NewsletterBanner'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    maxWidth: 840,
  },
  heading: {
    [theme.breakpoints.up('sm')]: {
      textTransform: 'uppercase',
    },
  },
  subHeading: {
    maxWidth: 308,
    [theme.breakpoints.up('sm')]: {
      maxWidth: 550,
    },
  },
  hiddenInMobile: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  visibleInMobile: {
    display: 'none',
    [theme.breakpoints.down('md')]: {
      display: 'block',
    },
  },
}))

interface Props {
  venue: Venue
  eventId?: string
  isOrderConfirmed: boolean
  hasBooking: boolean
  hasSelfCheckIn: boolean
  invoiceDownloadLink?: string | null
  wasSomethingPayed: boolean
  voucherCode?: string
  customer: Customer | null
}

const ConfirmationComponent: React.FC<React.PropsWithChildren<Props>> = ({
  venue,
  eventId,
  hasBooking,
  isOrderConfirmed,
  hasSelfCheckIn,
  invoiceDownloadLink,
  wasSomethingPayed,
  voucherCode,
  customer,
}) => {
  const classes = useStyles()

  return (
    <Page>
      <Flex justifyContent="center" marginBottom="hero" fullWidth data-testid="confirmation-page">
        <Flex flexDirection="column" className={classes.container}>
          <motion.div
            initial={{ opacity: 0, y: '-100vh' }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ease: 'easeInOut' }}
          >
            <Flex
              alignItems="center"
              flexDirection="column"
              verticalSpacing="wide"
              marginTop="wide"
              marginBottom="wide"
            >
              <motion.span
                initial={{ scale: 0.25, opacity: 0 }}
                animate={{ scale: 1, opacity: 1, transition: { ease: 'easeInOut', duration: 0.5, delay: 0.25 } }}
              >
                <Icon icon={Icons.SUCCESS} size="xxx-large" color="primary.main" />
              </motion.span>
              <Heading is="h1" variant="large" textAlign="center" className={classes.heading}>
                <ConfirmationHeading
                  isOrderConfirmed={isOrderConfirmed}
                  hasBooking={hasBooking}
                  wasSomethingPayed={wasSomethingPayed}
                  venue={venue.name}
                />
              </Heading>
              <Text textAlign="center" fontWeight="bold" marginBottom="wide" className={classes.subHeading}>
                <ConfirmationSubHeading
                  isOrderConfirmed={isOrderConfirmed}
                  hasBooking={hasBooking}
                  hasSelfCheckIn={hasSelfCheckIn}
                />
              </Text>
            </Flex>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: '200vh' }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ease: 'easeInOut' }}
          >
            <Flex flexDirection="column" verticalSpacing="wide">
              <ConfirmationActions
                venue={venue}
                eventId={eventId}
                hasBooking={hasBooking}
                invoiceDownloadLink={invoiceDownloadLink}
                voucherCode={voucherCode}
              />
              <div className={classes.hiddenInMobile}>
                <DesktopBookingDetails />
              </div>
              {customer && !customer.isSubscribedToNewsletter && (
                <NewsletterBanner venue={venue} customerId={customer.id} />
              )}
              <AppPromotion venue={venue} hasSelfCheckIn={hasSelfCheckIn} />
            </Flex>
          </motion.div>
        </Flex>
      </Flex>
      <div className={classes.visibleInMobile}>
        <BottomFixedCollapsibleSection collapsibleContent={<MobileBookingDetails />} showExpandIcon hideHeaderOnExpand>
          <Text textAlign="center" fontWeight="bold" paddingTop="default">
            <MobileConfirmationCollapsibleCardHeader hasBooking={hasBooking} />
          </Text>
        </BottomFixedCollapsibleSection>
      </div>
    </Page>
  )
}

export default ConfirmationComponent
